import React from 'react';
import { BarChart3 } from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import { formatCurrency, formatNumber } from '../../../utils/analysis.utils';
import { useTranslation } from 'react-i18next';

interface PriceDistributionProps {
  analysis: any;
  retailTariffValue: number;
  prices: any;
  priceDistribution: any[];
  priceDistributionData: any;
  priceDistributionOptions: any;
}

export const PriceDistribution: React.FC<PriceDistributionProps> = ({
  analysis,
  retailTariffValue,
  prices,
  priceDistribution,
  priceDistributionData,
  priceDistributionOptions
}) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
        <h3 className="text-base font-semibold text-black flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-brand-primary" />
          </div>
          {t('optimizationAnalysis.theoretical.priceDistribution.title')}
        </h3>
      </div>
      <div className="p-6">
        <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">{t('optimizationAnalysis.theoretical.priceDistribution.retailTariff', { value: formatNumber(retailTariffValue, 2) })}</p>
              <p className="text-xs text-gray-600 mt-1" dangerouslySetInnerHTML={{
                __html: t('optimizationAnalysis.theoretical.priceDistribution.hoursBelowRetail', {
                  hours: formatNumber(analysis?.hoursBelowRetail || 0),
                  percent: formatNumber(analysis?.percentBelowRetail || 0, 1)
                })
              }} />
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-600">{t('optimizationAnalysis.theoretical.priceDistribution.arbitrageOpportunity')}</p>
              <p className="text-2xl font-bold text-brand-primary">{formatNumber(analysis?.percentBelowRetail || 0, 1)}%</p>
            </div>
          </div>
        </div>

        {analysis?.theoretical?.hoursNeeded && (
          <div className="mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-1 h-8 bg-brand-primary rounded"></div>
                <div>
                  <p className="text-xs font-semibold text-gray-900">{t('optimizationAnalysis.theoretical.priceDistribution.consumptionHoursUsed')}</p>
                  <p className="text-sm text-gray-700">
                    {t('optimizationAnalysis.theoretical.priceDistribution.hoursAtPrices', {
                      hours: formatNumber(analysis.theoretical.hoursNeeded),
                      price: formatNumber(analysis.theoretical.cutoffPrice, 1)
                    })}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {t('optimizationAnalysis.theoretical.priceDistribution.allConsumptionCovered', {
                      percent: formatNumber((analysis.theoretical.hoursNeeded / (analysis.hoursBelowRetail || 1)) * 100, 0)
                    })}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-600">{t('optimizationAnalysis.theoretical.priceDistribution.usage')}</p>
                <p className="text-lg font-bold text-gray-900">
                  {formatNumber((analysis.theoretical.hoursNeeded / (prices?.data?.length || 1)) * 100, 1)}%
                </p>
              </div>
            </div>
          </div>
        )}

        {analysis?.theoretical?.hoursNeeded && analysis?.theoretical?.cutoffPrice < retailTariffValue && (
          <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-700">
              <span dangerouslySetInnerHTML={{ __html: t('optimizationAnalysis.theoretical.priceDistribution.whyOnlyPrice', {
                cutoffPrice: formatNumber(analysis.theoretical.cutoffPrice, 1),
                retailPrice: formatNumber(retailTariffValue, 1)
              }) }} />
              <br />
              {t('optimizationAnalysis.theoretical.priceDistribution.explanationText', {
                mwh: formatNumber(analysis.totalConsumption / 1000, 0),
                hours: formatNumber(analysis.theoretical.hoursNeeded),
                cutoffPrice: formatNumber(analysis.theoretical.cutoffPrice, 1),
                remaining: formatNumber((analysis.hoursBelowRetail || 0) - analysis.theoretical.hoursNeeded),
                retailPrice: formatNumber(retailTariffValue, 1)
              })}
            </p>
          </div>
        )}

        <div className="h-64">
          {priceDistribution && priceDistribution.length > 0 ? (
            <Bar data={priceDistributionData} options={priceDistributionOptions} />
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-sm">{t('optimizationAnalysis.theoretical.priceDistribution.noDataAvailable')}</p>
            </div>
          )}
        </div>

        <div className="mt-3 flex items-center justify-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-gray-400 rounded"></div>
            <span className="text-gray-600">{t('optimizationAnalysis.theoretical.priceDistribution.legendZeroPrice')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-gray-600 rounded"></div>
            <span className="text-gray-600">{t('optimizationAnalysis.theoretical.priceDistribution.legendBelowRetail')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-gray-700 rounded"></div>
            <span className="text-gray-600">{t('optimizationAnalysis.theoretical.priceDistribution.legendSpanningRetail')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-gray-800 rounded"></div>
            <span className="text-gray-600">{t('optimizationAnalysis.theoretical.priceDistribution.legendAboveRetail')}</span>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-700 font-semibold">{t('optimizationAnalysis.theoretical.priceDistribution.zeroPrice')}</p>
            <p className="text-lg font-bold text-gray-900">
              {formatNumber(priceDistribution?.find(b => b.range.startsWith('0'))?.count || 0)}
            </p>
            <p className="text-xs text-gray-600">
              {((priceDistribution?.find(b => b.range.startsWith('0'))?.count || 0) / (prices?.data?.length || 1) * 100).toFixed(1)}% {t('optimizationAnalysis.theoretical.priceDistribution.ofHours')}
            </p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-700 font-semibold">{t('optimizationAnalysis.theoretical.priceDistribution.below30')}</p>
            <p className="text-lg font-bold text-gray-900">
              {formatNumber(priceDistribution?.filter(b => {
                const start = parseInt(b.range.split('-')[0]);
                return start < 30;
              }).reduce((sum, b) => sum + b.count, 0) || 0)}
            </p>
            <p className="text-xs text-gray-600">
              {((priceDistribution?.filter(b => {
                const start = parseInt(b.range.split('-')[0]);
                return start < 30;
              }).reduce((sum, b) => sum + b.count, 0) || 0) / (prices?.data?.length || 1) * 100).toFixed(1)}% {t('optimizationAnalysis.theoretical.priceDistribution.ofHours')}
            </p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-700 font-semibold">{t('optimizationAnalysis.theoretical.priceDistribution.range3070')}</p>
            <p className="text-lg font-bold text-gray-900">
              {formatNumber(priceDistribution?.filter(b => {
                const start = parseInt(b.range.split('-')[0]);
                return start >= 30 && start < 70;
              }).reduce((sum, b) => sum + b.count, 0) || 0)}
            </p>
            <p className="text-xs text-gray-600">
              {((priceDistribution?.filter(b => {
                const start = parseInt(b.range.split('-')[0]);
                return start >= 30 && start < 70;
              }).reduce((sum, b) => sum + b.count, 0) || 0) / (prices?.data?.length || 1) * 100).toFixed(1)}% {t('optimizationAnalysis.theoretical.priceDistribution.ofHours')}
            </p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-700 font-semibold">{t('optimizationAnalysis.theoretical.priceDistribution.above70')}</p>
            <p className="text-lg font-bold text-gray-900">
              {formatNumber(priceDistribution?.filter(b => {
                const start = parseInt(b.range.split('-')[0]);
                return start >= 70;
              }).reduce((sum, b) => sum + b.count, 0) || 0)}
            </p>
            <p className="text-xs text-gray-600">
              {((priceDistribution?.filter(b => {
                const start = parseInt(b.range.split('-')[0]);
                return start >= 70;
              }).reduce((sum, b) => sum + b.count, 0) || 0) / (prices?.data?.length || 1) * 100).toFixed(1)}% {t('optimizationAnalysis.theoretical.priceDistribution.ofHours')}
            </p>
          </div>
        </div>
        <div className="mt-4 bg-gray-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">
            {t('optimizationAnalysis.theoretical.priceDistribution.histogramNote', {
              percent: ((priceDistribution?.find(b => b.range.startsWith('0'))?.count || 0) / (prices?.data?.length || 1) * 100).toFixed(1)
            })}
          </p>
        </div>
      </div>
    </div>
  );
};